/**
 * /Recon authenticated portal shell.
 *
 * Wraps every authenticated route in a layout that swaps the public nav
 * for a portal-flavored one (engagements / documents / account links +
 * logout). Redirects to /portal/login if no access token.
 *
 * Visual rules: same /Recon palette, but the portal context shifts the
 * nav primary to "/recon CLIENT PORTAL" so the user knows they're in a
 * different part of the product than the public marketing surface.
 */
import { Link, NavLink, Navigate, Outlet, useLocation } from "react-router-dom";
import { useClientAuth } from "@/auth/client-auth-context";
import "./portal-shell.css";

export function PortalShell() {
  const { isAuthenticated, isBootstrapping, logout } = useClientAuth();
  const location = useLocation();

  // The bootstrap /refresh call is in flight on initial mount. Show a
  // neutral loading state instead of bouncing to login — otherwise users
  // who refresh a portal page see a flash of "Sign in" before the
  // cookie-driven refresh resolves and they land back where they were.
  if (isBootstrapping) {
    return (
      <div className="portal-shell">
        <main className="recon-main">
          <div style={{ padding: "var(--space-12) var(--space-6)", textAlign: "center", color: "var(--fg3)" }}>
            Loading…
          </div>
        </main>
      </div>
    );
  }

  if (!isAuthenticated) {
    // Preserve the intended destination so post-login can deep-link back
    return (
      <Navigate
        to="/portal/login"
        replace
        state={{ from: location.pathname }}
      />
    );
  }

  return (
    <div className="portal-shell">
      <header className="portal-nav">
        <div className="portal-nav-inner">
          <Link to="/portal" className="recon-brand" aria-label="Recon client portal home">
            <img src="/recon_flame.svg" alt="" className="recon-brand-flame" aria-hidden="true" />
            <span className="recon-brand-lockup">
              <img
                src="/wordmark_signumcyber.png"
                alt="SignumCyber"
                className="recon-brand-wordmark-img"
              />
              <span className="recon-brand-recon">/recon</span>
              <span className="portal-nav-pill">CLIENT PORTAL</span>
            </span>
          </Link>
          <nav className="recon-nav-links" aria-label="Portal sections">
            <NavLink to="/portal" end className={navClass}>Engagements</NavLink>
            <NavLink to="/portal/documents" className={navClass}>Documents</NavLink>
            <NavLink to="/portal/trusted-recipients" className={navClass}>Trusted recipients</NavLink>
            <NavLink to="/portal/account" className={navClass}>Account</NavLink>
          </nav>
          <div className="recon-nav-cta">
            <button
              type="button"
              className="recon-btn recon-btn-secondary"
              onClick={() => { void logout(); }}
            >
              Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="recon-main">
        <Outlet />
      </main>
    </div>
  );
}

function navClass({ isActive }: { isActive: boolean }) {
  return `recon-nav-link${isActive ? " recon-nav-link-active" : ""}`;
}
